import { useToast } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useState } from 'react'
import type { DateRange } from 'react-day-picker'
import { DatePicker } from '@/components'
import { RegularModal } from '@/components_deprecated'
import { DownloadTachoFilesEntitySelect } from './download-tacho-files-entity-select'
import { useDownloadTachoFilesZip } from './useDownloadTachoFilesZip'
import type { DownloadTachoFilesMode } from './types'

/** Mirrors the backend cap (integrations/views.py MAX_RANGE_DAYS). */
const MAX_RANGE_DAYS = 32

interface DownloadTachoFilesPromptProps {
  mode: DownloadTachoFilesMode
  /** Closes the prompt. Called only after a successful download or a cancel. */
  onClose: () => void
}

export const DownloadTachoFilesPrompt: React.FC<
  DownloadTachoFilesPromptProps
> = ({ mode, onClose }) => {
  const translations = useTranslate({
    title: 'activities.download-multiple.title',
    description: `activities.download-multiple.description.${mode}`,
    timespan: 'activities.download-multiple.timespan',
    users: 'common.users',
    vehicles: 'common.vehicles',
    optional: 'common.optional',
    usersHint: 'activities.download-multiple.users-hint',
    vehiclesHint: 'activities.download-multiple.vehicles-hint',
    submit: 'activities.download-multiple.submit',
    empty: 'activities.download-multiple.empty',
    error: 'activities.download-multiple.error',
    rangeTooLarge: 'activities.download-multiple.range-too-large',
  })

  const addToast = useToast()
  const { download, downloading } = useDownloadTachoFilesZip(mode)

  const [range, setRange] = useState<DateRange | null>(null)
  const [ids, setIds] = useState<string[]>([])

  const isVehicle = mode === 'vehicle'
  const canSubmit = !!range?.from && !!range.to

  async function handleSubmit() {
    if (!range?.from || !range.to) return

    const result = await download({
      dateStart: range.from,
      dateEnd: range.to,
      ids,
    })

    // Keep the prompt open on failure or an empty result so the user can adjust.
    if (!result.ok) return addToast('error', translations.error)
    if (result.empty) return addToast('info', translations.empty)

    onClose()
  }

  return (
    <RegularModal
      title={translations.title}
      submitText={translations.submit}
      submitDisabled={!canSubmit}
      loading={downloading}
      width="480px"
      cancelOnEscape
      onClose={onClose}
      onCancel={onClose}
      onSubmit={handleSubmit}
    >
      <div className="flex flex-col gap-5">
        <p className="m-0 text-sm text-tertiary">{translations.description}</p>

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-semibold text-primary">
            {translations.timespan}
          </label>
          <DatePicker
            mode="range"
            value={range}
            selectionMode="continuous"
            format="d MMM yyyy"
            maxRangeDays={MAX_RANGE_DAYS}
            onChange={setRange}
          />
          <span className="text-xs text-tertiary">
            {translations.rangeTooLarge}
          </span>
        </div>

        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <label className="text-sm font-semibold text-primary">
              {isVehicle ? translations.vehicles : translations.users}
            </label>
            <span className="text-xs text-tertiary">
              {translations.optional}
            </span>
          </div>
          <DownloadTachoFilesEntitySelect
            mode={mode}
            value={ids}
            onChange={setIds}
          />
          <span className="text-xs text-tertiary">
            {isVehicle ? translations.vehiclesHint : translations.usersHint}
          </span>
        </div>
      </div>
    </RegularModal>
  )
}
